import { create } from "zustand";

/**
 * Blackhole websocket client. One socket for the whole console,
 * reference-counted bindings keyed by (account, binding), full
 * re-subscribe after a reconnect, promise-wrapped replies correlated on
 * request_id. Protocol notes: doc/engineering/switchboard/realtime-blackhole.md.
 */

export interface SocketEvent {
  action: "event";
  subscribed_key: string;
  subscription_key: string;
  name: string;
  routing_key: string;
  data: Record<string, unknown>;
}

interface Reply {
  action: "reply";
  request_id: string;
  status: "success" | "error";
  data: { errors?: string[]; subscribed?: string[]; unsubscribed?: string[]; subscriptions?: string[]; response?: string };
}

export type Handler = (event: SocketEvent) => void;
export type ConnectionState = "idle" | "connecting" | "open" | "reconnecting" | "closed";

interface SocketStore {
  state: ConnectionState;
  lastError?: string;
  /**
   * The socket is open but at least one binding is not subscribed: a
   * reply was dropped (blackhole's session mailbox silently drops over
   * `max_queued_messages`) or refused. Events for that binding are not
   * arriving, so a screen that lives on them should say it is showing
   * polled data.
   */
  degraded: boolean;
}
export const useSocketState = create<SocketStore>(() => ({ state: "idle", degraded: false }));

const PING_INTERVAL_MS = 45_000;
const REPLY_TIMEOUT_MS = 10_000;
const BACKOFF_MIN_MS = 125;
const BACKOFF_MAX_MS = 60_000;
/** A subscribe whose reply never came is retried on its own clock: 2 s, 4 s, 8 s, … */
const SUBSCRIBE_RETRY_MIN_MS = 2_000;
const SUBSCRIBE_RETRY_MAX_MS = 30_000;

const bindingKey = (accountId: string, binding: string) => `${accountId}\u0000${binding}`;

class BlackholeClient {
  private ws?: WebSocket;
  private url = "";
  private token?: () => string | undefined;
  private onAuthFailure?: () => Promise<boolean>;
  private shouldRun = false;
  private backoff = BACKOFF_MIN_MS;
  private pingTimer?: number;
  private reconnectTimer?: number;
  private pending = new Map<string, { resolve: (r: Reply) => void; reject: (e: Error) => void; timer: number }>();
  private handlers = new Map<string, Set<Handler>>();
  private subscribed = new Set<string>();
  /** Bindings whose subscribe did not take, with the retry timer and attempt count each. */
  private failed = new Map<string, { timer?: number; attempts: number; error: string }>();
  /** Incremented per open()/disconnect() so a stale socket's close event cannot act on the live one. */
  private gen = 0;

  configure(opts: { url: string; token: () => string | undefined; onAuthFailure?: () => Promise<boolean> }) {
    this.url = opts.url;
    this.token = opts.token;
    this.onAuthFailure = opts.onAuthFailure;
  }

  connect() {
    this.shouldRun = true;
    this.open();
  }

  disconnect() {
    this.shouldRun = false;
    this.gen++;
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.pingTimer);
    this.subscribed.clear();
    this.clearAllFailures();
    this.ws?.close();
    this.ws = undefined;
    useSocketState.setState({ state: "closed", degraded: false, lastError: undefined });
  }

  /** Register a handler; sends `subscribe` only for the first one on this (account, binding). */
  bind(accountId: string, binding: string, handler: Handler): () => void {
    const key = bindingKey(accountId, binding);
    let set = this.handlers.get(key);
    if (!set) {
      set = new Set();
      this.handlers.set(key, set);
      void this.subscribe(accountId, binding);
    }
    set.add(handler);
    return () => {
      const s = this.handlers.get(key);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) {
        this.handlers.delete(key);
        void this.unsubscribe(accountId, binding);
      }
    };
  }

  private open() {
    if (!this.shouldRun || !this.url) return;
    useSocketState.setState({ state: this.backoff === BACKOFF_MIN_MS ? "connecting" : "reconnecting" });
    const ws = new WebSocket(this.url);
    const gen = ++this.gen;
    this.ws = ws;
    ws.onopen = () => {
      if (gen !== this.gen) return;
      this.backoff = BACKOFF_MIN_MS;
      useSocketState.setState({ state: "open", lastError: undefined, degraded: false });
      this.pingTimer = window.setInterval(() => void this.ping(), PING_INTERVAL_MS);
      this.subscribed.clear();
      // A fresh socket subscribes everything again, so any waiting retry is stale.
      this.clearAllFailures();
      for (const key of this.handlers.keys()) {
        const [accountId, binding] = key.split("\u0000");
        void this.subscribe(accountId, binding);
      }
    };
    ws.onmessage = (m) => this.onMessage(m);
    ws.onclose = () => {
      if (gen !== this.gen) return; // a newer socket owns the timers and pending replies
      window.clearInterval(this.pingTimer);
      for (const p of this.pending.values()) {
        window.clearTimeout(p.timer);
        p.reject(new Error("socket closed"));
      }
      this.pending.clear();
      this.subscribed.clear();
      this.clearAllFailures();
      if (this.ws === ws) this.ws = undefined;
      if (!this.shouldRun) return;
      useSocketState.setState({ state: "reconnecting", degraded: false });
      this.reconnectTimer = window.setTimeout(() => this.open(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private onMessage(m: MessageEvent) {
    let msg: Reply | SocketEvent | { action: string };
    try {
      msg = JSON.parse(String(m.data));
    } catch {
      return;
    }
    if (msg.action === "reply") {
      const reply = msg as Reply;
      const p = this.pending.get(reply.request_id);
      if (!p) return;
      this.pending.delete(reply.request_id);
      window.clearTimeout(p.timer);
      p.resolve(reply);
      return;
    }
    if (msg.action === "event") {
      const ev = msg as SocketEvent;
      // Fan out to the handlers of this binding that are watching the
      // event's account. One socket follows an admin across accounts, so
      // an event whose account cannot be told (a call leg published before
      // its account channel var landed, say) goes only to handlers bound
      // with the "*" wildcard, never to another account's screen.
      const accountId = eventAccountId(ev);
      for (const [key, set] of this.handlers) {
        const [acc, binding] = key.split("\u0000");
        if (binding !== ev.subscribed_key) continue;
        if (acc !== "*" && acc !== accountId) continue;
        for (const h of set) h(ev);
      }
    }
  }

  private send(payload: Record<string, unknown>): Promise<Reply> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error("socket not open"));
    const request_id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(request_id);
        reject(new Error("no reply from blackhole"));
      }, REPLY_TIMEOUT_MS);
      this.pending.set(request_id, { resolve, reject, timer });
      ws.send(JSON.stringify({ request_id, auth_token: this.token?.(), ...payload }));
    });
  }

  private isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Drop a binding's retry, whether it is waiting or was never scheduled. */
  private clearFailure(key: string) {
    const failure = this.failed.get(key);
    if (!failure) return;
    if (failure.timer !== undefined) window.clearTimeout(failure.timer);
    this.failed.delete(key);
    this.publishDegraded();
  }

  private clearAllFailures() {
    for (const failure of this.failed.values()) if (failure.timer !== undefined) window.clearTimeout(failure.timer);
    this.failed.clear();
  }

  private publishDegraded() {
    const [first] = this.failed.values();
    useSocketState.setState({ degraded: this.failed.size > 0, lastError: first?.error });
  }

  /**
   * A subscribe that neither succeeded nor failed to authenticate is not
   * fatal and not self-healing either: the socket stays open, so nothing
   * re-sends it, and the binding is quietly dead. Try again with a
   * widening gap, for as long as something is still listening.
   */
  private retrySubscribe(accountId: string, binding: string, error: string) {
    const key = bindingKey(accountId, binding);
    if (!this.handlers.has(key)) return this.clearFailure(key);
    const previous = this.failed.get(key);
    if (previous?.timer !== undefined) return; // one is already waiting
    const attempts = (previous?.attempts ?? 0) + 1;
    const delay = Math.min(SUBSCRIBE_RETRY_MIN_MS * 2 ** (attempts - 1), SUBSCRIBE_RETRY_MAX_MS);
    const timer = window.setTimeout(() => {
      const failure = this.failed.get(key);
      if (failure) failure.timer = undefined;
      // Between the failure and now the screen may have gone, or the socket
      // may have dropped — a reconnect re-subscribes everything itself.
      if (!this.handlers.has(key)) return this.clearFailure(key);
      if (!this.isOpen()) return;
      void this.subscribe(accountId, binding);
    }, delay);
    this.failed.set(key, { timer, attempts, error });
    this.publishDegraded();
  }

  private async subscribe(accountId: string, binding: string) {
    const key = bindingKey(accountId, binding);
    if (this.subscribed.has(key)) return;
    try {
      const reply = await this.send({
        action: "subscribe",
        name: "switchboard",
        metadata: { route: window.location.pathname, version: __CONSOLE_VERSION__ },
        data: { account_id: accountId, binding },
      });
      if (reply.status === "success") {
        this.subscribed.add(key);
        this.clearFailure(key);
        // Every handler may have unbound while the reply was in flight.
        if (!this.handlers.has(key)) void this.unsubscribe(accountId, binding);
        return;
      }
      const errors = reply.data?.errors ?? [];
      const message = errors.join("; ") || "subscribe refused";
      if (errors.some((e) => e.startsWith("failed to authenticate token")) && this.onAuthFailure) {
        useSocketState.setState({ lastError: message });
        if (await this.onAuthFailure()) void this.subscribe(accountId, binding);
        return;
      }
      this.retrySubscribe(accountId, binding, message);
    } catch (err) {
      // "socket not open" is the reconnect's business: onopen re-subscribes
      // the whole handler map. A dropped or timed-out reply is ours.
      if (!this.isOpen()) return;
      this.retrySubscribe(accountId, binding, err instanceof Error ? err.message : String(err));
    }
  }

  private async unsubscribe(accountId: string, binding: string) {
    const key = bindingKey(accountId, binding);
    this.clearFailure(key);
    if (!this.subscribed.has(key)) return;
    this.subscribed.delete(key);
    try {
      await this.send({ action: "unsubscribe", data: { account_id: accountId, binding } });
    } catch {
      /* socket gone; nothing to undo */
    }
  }

  private async ping() {
    try {
      await this.send({ action: "ping" });
    } catch {
      this.ws?.close();
    }
  }
}

/**
 * Where the account sits in the routing key, per binding, for the families
 * whose payload does not carry it. Read from the publishers:
 *
 * | binding | routing key | account |
 * |---|---|---|
 * | `call.{EVENT}.{CALL_ID}` | `call.{ACCOUNT_ID}.{EVENT}.{CALL_ID}` (blackhole_listener) | 1 |
 * | `conference.event.…` | `{EVENT}.{ACCOUNT_ID}.{CONFERENCE_ID}.{CALL_ID}` (kapi_conference) | 1 |
 * | `fax.status.{FAX_ID}` | `fax.status.{ACCOUNT_ID}.{FAX_ID}` (kapi_fax) | 2 |
 * | `stream.{EVENT}.{CALL_ID}` | `pivot.stream.{EVENT}.{ACCOUNT_ID}.{CALL_ID}` (kapi_pivot) | 3 |
 * | `callcenter.agent\|queue\|call.{ID}` | `callcenter.{FAMILY}.{ACCOUNT_ID}.{ID}` (kapi_callcenter) | 2 |
 *
 * Absent on purpose: `object.*` and `fax.object.*`, whose key carries the
 * account *database* and whose payload carries `account_id`; and
 * `presence.*`, which is realm-scoped and names no account anywhere.
 */
const ACCOUNT_SEGMENT: [prefix: string, segment: number][] = [
  ["call.", 1],
  ["conference.event.", 1],
  ["fax.status.", 2],
  ["stream.", 3],
  // The call center payloads all carry `Account-ID`, so this is the
  // fallback for one that does not rather than the usual road in.
  ["callcenter.agent.", 2],
  ["callcenter.queue.", 2],
  ["callcenter.call.", 2],
];

/** The account an event belongs to, or "" when it cannot be told. */
export function eventAccountId(ev: Pick<SocketEvent, "data" | "routing_key" | "subscribed_key">): string {
  const top = ev.data?.account_id;
  if (typeof top === "string" && top && top !== "undefined") return top;
  const ccv = ev.data?.custom_channel_vars as Record<string, unknown> | undefined;
  const inner = ccv?.account_id;
  if (typeof inner === "string" && inner && inner !== "undefined") return inner;
  const at = ACCOUNT_SEGMENT.find(([prefix]) => ev.subscribed_key?.startsWith(prefix))?.[1];
  if (at !== undefined) {
    const seg = ev.routing_key?.split(".")[at];
    // Carrier-inbound legs are published as `call.undefined.…` until the
    // account channel var lands; `*` is the wildcard, not an account.
    if (seg && seg !== "undefined" && seg !== "*") return seg;
  }
  return "";
}

export const socket = new BlackholeClient();

/**
 * Where blackhole answers. Same-origin `/socket` (nginx proxies it, and
 * so does the dev server) unless the session named an origin of its own:
 * a portal session is served from the portal, not from the cluster, and
 * the mint told it which cluster to talk to. A named origin may be given
 * as `ws(s)://` or `http(s)://`, with or without the path blackhole sits
 * under; with no path it is assumed to be `/socket`, as every deployed
 * cluster fronts it that way.
 */
export function socketUrl(origin?: string): string {
  const named = origin?.trim().replace(/\/+$/, "");
  if (named) {
    const withScheme = /^[a-z]+:\/\//i.test(named) ? named : `wss://${named}`;
    const url = new URL(withScheme.replace(/^http(s?):/i, "ws$1:"));
    const path = url.pathname === "/" ? "/socket" : url.pathname.replace(/\/+$/, "");
    return `${url.protocol === "wss:" ? "wss:" : "ws:"}//${url.host}${path}`;
  }
  const proto = window.location.protocol === "https:" ? "wss://" : "ws://";
  return `${proto}${window.location.host}/socket`;
}
